const CURSOR_REF_ATTR = "data-cursor-ref";

export function stripCursorBrowserRefs(root: ParentNode): void {
  if (root instanceof Element && root.hasAttribute(CURSOR_REF_ATTR)) {
    root.removeAttribute(CURSOR_REF_ATTR);
  }
  for (const element of root.querySelectorAll(`[${CURSOR_REF_ATTR}]`)) {
    element.removeAttribute(CURSOR_REF_ATTR);
  }
}

export function isCursorHydrationNoise(args: unknown[]): boolean {
  const text = args.map(argToText).join("\n");
  if (!text.includes(CURSOR_REF_ATTR)) return false;
  return (
    text.includes("hydrat") ||
    text.includes("did not match") ||
    text.includes("server rendered HTML")
  );
}

export function watchCursorBrowserRefsUntilIdle(
  root: ParentNode,
  options?: { idleTimeoutMs?: number },
): () => void {
  stripCursorBrowserRefs(root);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (
        mutation.type === "attributes" &&
        mutation.attributeName === CURSOR_REF_ATTR &&
        mutation.target instanceof Element
      ) {
        mutation.target.removeAttribute(CURSOR_REF_ATTR);
      }
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) {
          stripCursorBrowserRefs(node);
        }
      }
    }
  });

  observer.observe(root, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: [CURSOR_REF_ATTR],
  });

  const stop = (): void => observer.disconnect();
  const idleTimeoutMs = options?.idleTimeoutMs ?? 2500;
  const requestIdle =
    typeof globalThis.requestIdleCallback === "function"
      ? globalThis.requestIdleCallback.bind(globalThis)
      : null;
  if (requestIdle) {
    requestIdle(stop, { timeout: idleTimeoutMs });
  } else {
    setTimeout(stop, idleTimeoutMs);
  }
  return stop;
}

export function silenceCursorHydrationNoise(): void {
  const original = console.error.bind(console);
  console.error = (...args: Parameters<typeof console.error>) => {
    if (isCursorHydrationNoise(args)) return;
    original(...args);
  };
}

export function installCursorBrowserHydrationGuard(): void {
  if (typeof document === "undefined") return;
  watchCursorBrowserRefsUntilIdle(document.documentElement);
  silenceCursorHydrationNoise();
}

function argToText(arg: unknown): string {
  if (typeof arg === "string") return arg;
  if (arg instanceof Error) return arg.message;
  return "";
}
